/**
 * Compact "Agent typing" indicator with three pulsing dots.
 *
 * Shown by the chat surface during the brief window after a user sends a
 * message but before the first SSE event arrives — i.e. before the activity
 * timeline has any tool events to render and before the assistant text starts
 * streaming. Once either of those signals lands, the indicator is replaced.
 *
 * The optional `toolCall` prop is preserved so legacy callers (agent-chat-tab,
 * AppBuilder) that don't yet use the activity timeline can still surface a
 * single in-flight tool name beside the dots.
 */

import {
  Globe,
  Terminal,
  FileText,
  Database,
  Search,
  Mail,
  Code,
  FolderOpen,
  Pencil,
  Eye,
  type LucideIcon,
} from "lucide-react";

const TOOL_ICONS: Record<string, LucideIcon> = {
  web_search: Globe,
  web_fetch: Globe,
  browser: Globe,
  exec: Terminal,
  bash: Terminal,
  read: FileText,
  read_file: FileText,
  write: Pencil,
  write_file: Pencil,
  edit_file: Pencil,
  list_directory: FolderOpen,
  glob: FolderOpen,
  grep: Search,
  search: Search,
  memory_search: Database,
  database: Database,
  sql: Database,
  email: Mail,
  send_email: Mail,
  code: Code,
  image: Eye,
  screenshot: Eye,
};

function getToolIcon(toolName: string): LucideIcon | null {
  const lower = toolName.toLowerCase().replace(/[^a-z_]/g, "");
  for (const [key, icon] of Object.entries(TOOL_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return null;
}

function formatToolName(name: string): string {
  return name
    .replace(/^mcp__[^_]+__/, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase();
}

export function ThinkingIndicator({ toolCall }: { toolCall?: string | null }) {
  const hasTool = !!toolCall && toolCall !== "Working" && !toolCall.startsWith("Working (");
  const ToolIcon = hasTool ? getToolIcon(toolCall) : null;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2 text-xs text-text-muted">
        <span className="inline-flex items-center gap-1" aria-hidden>
          <span className="typing-dot h-1.5 w-1.5 rounded-full bg-accent" style={{ animationDelay: "0ms" }} />
          <span className="typing-dot h-1.5 w-1.5 rounded-full bg-accent" style={{ animationDelay: "200ms" }} />
          <span className="typing-dot h-1.5 w-1.5 rounded-full bg-accent" style={{ animationDelay: "400ms" }} />
        </span>
        <span>Agent typing</span>
      </span>
      {hasTool && (
        <span className="inline-flex items-center gap-1.5 text-[11px]">
          {ToolIcon && <ToolIcon size={11} className="shrink-0 text-accent" />}
          <span className="font-mono text-accent bg-accent/10 px-1.5 py-0.5 rounded">
            {formatToolName(toolCall)}
          </span>
        </span>
      )}
    </div>
  );
}
